/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable no-underscore-dangle */
/* eslint-disable @angular-eslint/no-host-metadata-property */
import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  InjectionToken,
  Input,
  Optional,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';

/** Possible mode for a progress spinner. */
export type ProgressSpinnerMode = 'determinate' | 'indeterminate';

/** Default `n-progress-spinner` options that can be overridden. */
export interface MatProgressSpinnerDefaultOptions {
  /** Diameter of the spinner. */
  diameter?: number;
  /** Width of the spinner's stroke. */
  strokeWidth?: number;
  /**
   * Whether the animations should be force to be enabled, ignoring if the current environment is
   * using NoopAnimationsModule.
   */
  _forceAnimations?: boolean;
}

/** Injection token to be used to override the default options for `mat-progress-spinner`. */
export const MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS = new InjectionToken<MatProgressSpinnerDefaultOptions>(
  'mat-progress-spinner-default-options',
  {
    providedIn: 'root',
    factory: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY,
  },
);

/** @docs-private */
export function MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY(): MatProgressSpinnerDefaultOptions {
  return { diameter: BASE_SIZE };
}

/**
 * Base reference size of the spinner.
 */
const BASE_SIZE = 100;

/**
 * Base reference stroke width of the spinner.
 */
const BASE_STROKE_WIDTH = 10;

@Component({
  selector: 'n-progress-spinner',
  templateUrl: './n-progress-spinner.component.html',
  exportAs: 'nProgressSpinner',
  host: {
    role: 'progressbar',
    class: 'n-progress-spinner n-circular-progress',
    // set tab index to -1 so screen readers will read the aria-label
    // Note: there is a known issue with JAWS that does not read progressbar aria labels on FireFox
    tabindex: '-1',
    '[class.n-circular-progress--indeterminate]': 'mode === "indeterminate"',
    '[style.width.px]': 'diameter',
    '[style.height.px]': 'diameter',
    '[attr.aria-valuemin]': 'minValue',
    '[attr.aria-valuemax]': 'maxValue',
    '[attr.aria-valuenow]': 'mode === "determinate" ? value : null',
    '[attr.mode]': 'mode',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY, useValue: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS },
  ],
  styleUrls: ['./n-progress-spinner.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class NProgressSpinnerComponent {
  @Input() isPercentageValue = true;
  @Input() isValueVisible = false;
  @Input() label = $localize`Postęp`;
  // @HostBinding('')
  /** Whether the _n-animation-noopable class should be applied, disabling animations.  */
  //
  /** The element of the determinate spinner. */
  @ViewChild('determinateSpinner') _determinateCircle: ElementRef<HTMLElement>;

  constructor(
    readonly elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) animationMode: string,
    @Inject(MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS_FACTORY) defaults?: MatProgressSpinnerDefaultOptions,
  ) {
    if (defaults) {
      if (defaults.diameter) {
        this.diameter = defaults.diameter;
      }

      if (defaults.strokeWidth) {
        this.strokeWidth = defaults.strokeWidth;
      }
    }
  }

  /**
   * Mode of the progress bar.
   *
   * Input must be one of these values: determinate, indeterminate, buffer, query, defaults to
   * 'determinate'.
   * Mirrored to mode attribute.
   */
  @Input() mode: ProgressSpinnerMode =
    this.elementRef.nativeElement.nodeName.toLowerCase() === 'n-spinner' ? 'indeterminate' : 'determinate';

  @Input() maxValue = 100;

  /** Value of the progress bar. Defaults to zero. Mirrored to aria-valuenow. */
  @Input()
  get value(): number {
    return this.mode === 'determinate' ? this._value : 0;
  }
  set value(v: NumberInput) {
    this._value = Math.max(0, Math.min(100, coerceNumberProperty(v)));
  }
  private _value = 0;

  /** The diameter of the progress spinner (will set width and height of svg). */
  @Input()
  get diameter(): number {
    return this._diameter;
  }
  set diameter(size: NumberInput) {
    this._diameter = coerceNumberProperty(size);
  }
  private _diameter = BASE_SIZE;

  /** Stroke width of the progress spinner. */
  @Input()
  get strokeWidth(): number {
    return this._strokeWidth ?? this.diameter / 10;
  }
  set strokeWidth(value: NumberInput) {
    this._strokeWidth = coerceNumberProperty(value);
  }
  private _strokeWidth: number;

  /** The radius of the spinner, adjusted for stroke width. */
  circleRadius(): number {
    return (this.diameter - BASE_STROKE_WIDTH) / 2;
  }

  /** The view box of the spinner's svg element. */
  viewBox(): string {
    const viewBox = this.circleRadius() * 2 + this.strokeWidth;
    return `0 0 ${viewBox} ${viewBox}`;
  }

  /** The stroke circumference of the svg circle. */
  strokeCircumference(): number {
    return 2 * Math.PI * this.circleRadius();
  }

  /** The dash offset of the svg circle. */
  strokeDashOffset(): number | null {
    if (this.mode === 'determinate') {
      return (this.strokeCircumference() * (this.maxValue - this._value)) / this.maxValue;
    }
    return null;
  }

  /** Stroke width of the circle in percent. */
  circleStrokeWidth() {
    return (this.strokeWidth / this.diameter) * 100;
  }
}
